import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSpinner, 
  faCheck, 
  faTimes, 
  faUpload,
  faAnchor 
} from '@fortawesome/free-solid-svg-icons';
import './ProgressIndicator.css';

/**
 * Progress types for different contexts
 */
export const PROGRESS_TYPES = {
  SPINNER: 'spinner',
  BAR: 'bar',
  UPLOAD: 'upload',
  CIRCULAR: 'circular'
};

/**
 * Progress states
 */
export const PROGRESS_STATES = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error'
};

/**
 * Linear Progress Bar Component
 * Displays a horizontal progress bar with optional label
 */
export const ProgressBar = ({ 
  progress = 0, 
  state = PROGRESS_STATES.LOADING,
  label,
  showPercentage = true,
  height = 8,
  animated = true,
  className = ''
}) => {
  const clampedProgress = Math.min(100, Math.max(0, progress));
  
  return (
    <div className={`progress-bar-container ${className}`}>
      {label && (
        <div className="progress-bar-header">
          <span className="progress-bar-label">{label}</span>
          {showPercentage && (
            <span className="progress-bar-percentage">{Math.round(clampedProgress)}%</span>
          )}
        </div>
      )}
      <div 
        className={`progress-bar-track progress-bar-${state}`} 
        style={{ height: `${height}px` }}
      >
        <div 
          className={`progress-bar-fill ${animated ? 'animated' : ''}`}
          style={{ width: `${clampedProgress}%` }}
        />
      </div>
    </div>
  );
};

/**
 * Upload Progress Component
 * Specialized progress indicator for file uploads
 */
export const UploadProgress = ({
  progress = 0,
  state = PROGRESS_STATES.LOADING,
  fileName,
  fileSize,
  onCancel,
  className = ''
}) => {
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getIcon = () => {
    switch (state) {
      case PROGRESS_STATES.SUCCESS:
        return faCheck;
      case PROGRESS_STATES.ERROR:
        return faTimes;
      case PROGRESS_STATES.LOADING:
        return faUpload;
      default:
        return faUpload;
    }
  };

  return (
    <div className={`upload-progress ${state} ${className}`}>
      <div className="upload-progress-icon">
        <FontAwesomeIcon 
          icon={getIcon()} 
          spin={state === PROGRESS_STATES.LOADING}
        />
      </div>
      <div className="upload-progress-details">
        <div className="upload-progress-header">
          <span className="upload-progress-filename" title={fileName}>
            {fileName}
          </span>
          {fileSize && (
            <span className="upload-progress-size">{formatFileSize(fileSize)}</span>
          )}
        </div>
        <ProgressBar 
          progress={progress} 
          state={state}
          showPercentage={false}
          height={4}
        />
        <div className="upload-progress-footer">
          <span className="upload-progress-status">
            {state === PROGRESS_STATES.LOADING && `${Math.round(progress)}% uploaded`}
            {state === PROGRESS_STATES.SUCCESS && 'Upload complete!'}
            {state === PROGRESS_STATES.ERROR && 'Upload failed'}
          </span>
        </div>
      </div>
      {state === PROGRESS_STATES.LOADING && onCancel && (
        <button 
          className="upload-progress-cancel"
          onClick={onCancel}
          aria-label="Cancel upload"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}
    </div>
  );
};

/**
 * Spinner Component
 * Simple loading spinner for inline or overlay use
 */
export const Spinner = ({ 
  size = 'medium', 
  message,
  overlay = false,
  className = ''
}) => {
  const spinner = (
    <div className={`spinner spinner-${size} ${className}`}>
      <FontAwesomeIcon icon={faAnchor} spin className="spinner-icon" />
      {message && <span className="spinner-message">{message}</span>}
    </div>
  );

  if (overlay) {
    return (
      <div className="spinner-overlay">
        {spinner}
      </div>
    );
  }

  return spinner;
};

/**
 * Button with Loading State
 * A button that shows a spinner when loading
 */
export const LoadingButton = ({
  children,
  loading = false,
  loadingText,
  disabled = false,
  variant = 'primary',
  type = 'button',
  onClick,
  className = '',
  ...props
}) => {
  return (
    <button
      type={type}
      className={`btn btn-${variant} loading-button ${loading ? 'loading' : ''} ${className}`}
      disabled={disabled || loading}
      onClick={onClick}
      {...props}
    >
      {loading ? (
        <>
          <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
          {loadingText || children}
        </>
      ) : children}
    </button>
  );
};

/**
 * Page Transition Progress
 * Top-of-page progress bar for route transitions
 */
export const PageProgress = ({ 
  loading = false,
  progress = null, // null = indeterminate
  className = ''
}) => {
  if (!loading) return null;

  return (
    <div className={`page-progress ${className}`}>
      <div 
        className={`page-progress-bar ${progress === null ? 'indeterminate' : ''}`}
        style={progress !== null ? { width: `${progress}%` } : undefined}
      />
    </div>
  );
};

/**
 * Circular Progress Component
 * Displays progress in a circular format
 */
export const CircularProgress = ({
  progress = 0,
  size = 60,
  strokeWidth = 4,
  showPercentage = true,
  className = ''
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className={`circular-progress ${className}`} style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          className="circular-progress-track"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
        />
        <circle
          className="circular-progress-fill"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      {showPercentage && (
        <span className="circular-progress-text">{Math.round(progress)}%</span>
      )}
    </div>
  );
};

const ProgressIndicatorExports = {
  ProgressBar,
  UploadProgress,
  Spinner,
  LoadingButton,
  PageProgress,
  CircularProgress,
  PROGRESS_TYPES,
  PROGRESS_STATES
};

export default ProgressIndicatorExports;
